/*
  The Salon Foil Usage Calculator (a Back Room resource), SHOP-1205.

  Reads the boxes rendered by sections/main-usage-calculator.liquid and works
  out, on every keystroke:

    foils per period = sum of (how many x foils each), one row per service
    foils a week     = foils per period x days a week (average-day mode only)
                       x number of stylists
    foils a month    = foils a week x weeks worked a year / 12
    boxes a month    = foils a month / sheets per box
    boxes a year     = foils a week x weeks worked a year / sheets per box

  A month is weeks worked / 12, so at 52 weeks it is 4.33 weeks, never 4.

  The Foil Club suggestion: every pack x every delivery frequency is one
  option, expressed as boxes a month (pack boxes / months between
  deliveries). Two options giving the same boxes a month keep the smaller
  pack (Katie 03/10/2026: X6 monthly, not X12 every 2 months). Above the
  largest option, 2, 3 ... of the largest pack arrive monthly. A usage equal
  to an option shows that one option; a usage between two shows both, the
  nearer one tagged best fit, and a tie goes to the option that covers the
  usage so the salon never runs short. Below the smallest option, the
  smallest option is shown alone.

  Calculator type (data-uc-type, SHOP-1206): 'live' shows the member
  price, saving and start button; 'estimate' never mentions the Foil Club
  and shows the pack's normal price with a shop button (the product page
  with that pack selected); 'waitlist' shows no price and no button.

  Prints, pictures, stock and add to cart (SHOP-1221): with "More prints"
  set the section hands over one pack list per print and buttons above the
  packs swap between them; each pack can carry its variant's cut-out picture
  and its stock; a pack that cannot be bought shows the out-of-stock words on
  a disabled button; and "Adds the pack to the cart" turns the shop link into
  an add-to-cart button. Each one is off until its setting is set.

  Prices: the pack price is the variant's price and the member price is
  Shopify's own selling-plan allocation price, both handed over by the
  section in cents. Plans are matched to frequencies by the number of months
  in the plan's own wording ("1 month", "2 months", "Every 3 months";
  "month" with no number = 1). No plan, no member price, no saving and no
  start button: the pack, frequency and full price still show.

  Words: every customer-facing word comes from the section's settings, in
  the markup or in data-t-* attributes. "{n}" and "{pack}" in the
  "more than one pack" and "works out at" words are replaced with the
  number and the pack name; "{min}" and "{max}" (the pricing calculator's own placeholders) in the outside-the-range
  message with the box's limits. The only characters this file adds itself are
  number formatting and money, which goes through theme.Currency.formatMoney
  over the shop's own money format.

  Remembering: typed values are kept in localStorage on this device only;
  "Start again" clears them. Every read and write is wrapped, so a browser
  that blocks storage still calculates.
*/
(function () {
  'use strict';

  var STORE_KEY = 'salon-usage-calculator:v1';
  var EPS = 0.0001;

  function clean(str) {
    return String(str == null ? '' : str).replace(/[\s,]/g, '');
  }

  function parseNum(str) {
    var s = clean(str);
    if (s === '') return null;
    if (!/^-?(\d+\.?\d*|\.\d+)$/.test(s)) return NaN;
    var n = Number(s);
    return isFinite(n) ? n : NaN;
  }

  function moneyFormat() {
    var t = window.theme;
    return (t && t.settings && t.settings.moneyFormat) || '${{amount}}';
  }

  function money(cents) {
    if (typeof cents !== 'number' || !isFinite(cents)) return '';
    var cur = window.theme && window.theme.Currency;
    if (cur && cur.formatMoney) return cur.formatMoney(Math.round(cents), moneyFormat());
    return moneyFormat().replace(/\{\{\s*\w+\s*\}\}/, (Math.round(cents) / 100).toFixed(2));
  }

  function num(v, dp) {
    if (typeof v !== 'number' || !isFinite(v)) return '';
    return v.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  }

  function fill(words, n, pack) {
    return String(words || '').replace(/\{n\}/g, n).replace(/\{pack\}/g, pack);
  }

  function monthsIn(words) {
    var w = String(words || '').toLowerCase();
    if (w.indexOf('month') === -1) return null;
    var m = w.match(/(\d+)\s*-?\s*month/);
    if (m) return Number(m[1]);
    if (/\b(two|second)\b/.test(w)) return 2;
    if (/\b(three|third)\b/.test(w)) return 3;
    return 1;
  }

  // Every pack x frequency as boxes a month; equal values keep the smaller pack.
  function buildOptions(data) {
    var opts = [];
    (data.packs || []).forEach(function (pack, pi) {
      if (!(pack.boxes > 0) || !pack.name) return;
      (data.freqs || []).forEach(function (fq) {
        if (!fq.label) return;
        var plan = null;
        (pack.plans || []).forEach(function (pl) {
          if (!plan && monthsIn(pl.words) === fq.months) plan = pl;
        });
        opts.push({ pack: pack, packIndex: pi, freq: fq, qty: 1, plan: plan, perMonth: pack.boxes / fq.months });
      });
    });
    opts.sort(function (a, b) { return (a.perMonth - b.perMonth) || (a.pack.boxes - b.pack.boxes); });
    var out = [];
    opts.forEach(function (o) {
      var last = out[out.length - 1];
      if (last && Math.abs(last.perMonth - o.perMonth) < EPS) return;
      out.push(o);
    });
    return out;
  }

  // The largest monthly option, repeated, for usage above every option.
  function multiple(top, n) {
    return { pack: top.pack, packIndex: top.packIndex, freq: top.freq, qty: n, plan: top.plan, perMonth: top.perMonth * n };
  }

  // SHOP-1289: coverOnly (the section's "Only suggest packs that cover their
  // usage") never shows an option below the salon's usage: only the nearest
  // option at or above it, as the best fit (Katie 05/10/2026: a pack that
  // runs short is not a fit).
  function suggest(options, usage, coverOnly) {
    if (!options.length || !(usage > 0)) return [];
    var first = options[0];
    if (usage <= first.perMonth + EPS) return [{ opt: first, best: true, exact: Math.abs(usage - first.perMonth) < EPS }];
    var lower = null;
    var upper = null;
    for (var i = 0; i < options.length; i++) {
      if (Math.abs(options[i].perMonth - usage) < EPS) return [{ opt: options[i], best: true, exact: true }];
      if (options[i].perMonth < usage) lower = options[i];
      else { upper = options[i]; break; }
    }
    if (!upper) {
      var top = options[options.length - 1];
      if (top.freq.months !== 1) return [{ opt: top, best: true, exact: false }];
      var n = Math.floor(usage / top.perMonth + EPS);
      if (Math.abs(n * top.perMonth - usage) < EPS) return [{ opt: multiple(top, n), best: true, exact: true }];
      lower = n > 1 ? multiple(top, n) : top;
      upper = multiple(top, n + 1);
    }
    if (coverOnly) return [{ opt: upper, best: true, exact: false }];
    var dl = usage - lower.perMonth;
    var du = upper.perMonth - usage;
    var bestUpper = du <= dl + EPS;
    return [
      { opt: lower, best: !bestUpper, exact: false, side: 'lower' },
      { opt: upper, best: bestUpper, exact: false, side: 'upper' }
    ];
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  // SHOP-1221: "Adds the pack to the cart". Adds the pack (times the number
  // of packs the card suggests), then refreshes and opens the side cart the
  // way the theme's own add-to-cart does; on a cart-page store it goes to the
  // cart. If the add fails it falls back to the product page with the pack
  // selected, so the salon is never left with a dead button.
  function addToCart(btn, variant, qty, fallback) {
    btn.disabled = true;
    fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id: variant, quantity: qty || 1 }] }) })
      .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
      .then(function () {
        btn.disabled = false;
        if (window.theme && theme.settings && theme.settings.cartType === 'drawer') {
          document.dispatchEvent(new CustomEvent('cart:build'));
          document.dispatchEvent(new CustomEvent('cart:open'));
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(function () { btn.disabled = false; if (fallback) window.location.href = fallback; });
  }

  function init(root) {
    if (!root || root.hasAttribute('data-uc-ready')) return;
    root.setAttribute('data-uc-ready', '');

    var dataEl = root.querySelector('[data-uc-data]');
    var data = null;
    if (dataEl) { try { data = JSON.parse(dataEl.textContent); } catch (e) { data = null; } }
    var options = data ? buildOptions(data) : [];
    var plansBox = root.querySelector('[data-uc-plans]');
    var fields = Array.prototype.slice.call(root.querySelectorAll('[data-uc-field]'));
    var byKey = {};
    fields.forEach(function (f) { byKey[f.getAttribute('data-uc-field')] = f; });
    var modeBtns = Array.prototype.slice.call(root.querySelectorAll('[data-uc-mode]'));
    var modesRow = root.querySelector('[data-uc-modes]');
    var resetBtn = root.querySelector('[data-uc-reset]');
    var savedNote = root.querySelector('[data-uc-saved]');
    var emptyMsg = root.querySelector('[data-uc-empty]');
    var mode = 'day';
    var type = root.getAttribute('data-uc-type') || 'live';
    var t = function (name) { return root.getAttribute('data-t-' + name) || ''; };
    var action = root.getAttribute('data-uc-action') || 'page';
    var coverOnly = root.hasAttribute('data-uc-cover-only');

    var ranges = null;
    try { ranges = JSON.parse(root.getAttribute('data-uc-ranges') || 'null'); } catch (e) { ranges = null; }

    var starts = {};
    fields.forEach(function (f) { starts[f.getAttribute('data-uc-field')] = f.defaultValue; });

    function value(key) {
      var f = byKey[key];
      if (!f) return null;
      var n = parseNum(f.value);
      return (n === null || isNaN(n)) ? null : n;
    }

    function check(f) {
      if (!ranges) return;
      var kind = f.getAttribute('data-uc-check');
      var rule = ranges[kind];
      var msg = '';
      var n = parseNum(f.value);
      if (n !== null) {
        if (isNaN(n)) msg = t('check-number');
        else if (rule && rule[2] === 'whole' && Math.floor(n) !== n) msg = t('check-whole');
        else if (rule && (n < rule[0] || (rule[1] != null && n > rule[1]))) msg = t('check-range').replace(/\{min\}/g, rule[0]).replace(/\{max\}/g, rule[1]);
      }
      var holder = f.parentNode;
      var box = holder.querySelector('[data-uc-check-msg]');
      if (!msg) { if (box) box.parentNode.removeChild(box); return; }
      if (!box) {
        box = el('span', 'usage-calc__flag');
        box.setAttribute('data-uc-check-msg', '');
        box.appendChild(el('span', 'usage-calc__flag-text'));
        holder.appendChild(box);
      }
      box.firstChild.textContent = msg;
    }

    function setMode(m) {
      mode = m === 'week' ? 'week' : 'day';
      modeBtns.forEach(function (b) {
        var on = b.getAttribute('data-uc-mode') === mode;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-uc-when]'), function (n) {
        n.hidden = n.getAttribute('data-uc-when') !== mode;
      });
    }

    function compute() {
      var perPeriod = 0;
      var any = false;
      for (var i = 1; i <= 8; i++) {
        var c = value('count.' + i);
        var f = value('foils.' + i);
        if (c !== null && c > 0) any = true;
        if (c !== null && f !== null) perPeriod += c * f;
      }
      var days = mode === 'day' ? value('days') : 1;
      var stylists = byKey.stylists ? value('stylists') : 1;
      var weeks = value('weeks');
      if (days === null || stylists === null || weeks === null) any = false;
      var sheets = data && data.sheets > 0 ? data.sheets : null;
      var foilsWeek = perPeriod * (days || 0) * (stylists || 0);
      var foilsMonth = foilsWeek * (weeks || 0) / 12;
      return {
        any: any,
        foilsWeek: foilsWeek,
        foilsMonth: foilsMonth,
        boxesMonth: sheets ? foilsMonth / sheets : null,
        boxesYear: sheets ? foilsWeek * (weeks || 0) / sheets : null,
        days: days,
        stylists: stylists,
        weeks: weeks
      };
    }

    function optionTitle(o) {
      var pack = o.pack.name;
      return o.qty > 1 ? fill(t('multiple'), o.qty, pack) : pack;
    }

    function card(s) {
      var o = s.opt;
      var box = el('div', 'usage-calc__answer usage-calc__plan');
      // SHOP-1227: seven fixed rows, always present (an empty row is
      // harmless), so the section's subgrid lines every card's picture, tag,
      // name, frequency, price, works-out line and button up across cards.
      var rows = {};
      ['pic', 'flag', 'name', 'freq', 'price', 'per', 'action'].forEach(function (k) {
        rows[k] = box.appendChild(el('div', 'usage-calc__plan-row usage-calc__plan-row--' + k));
      });
      if (o.pack.img) {
        var pic = el('img', 'usage-calc__plan-img');
        pic.src = o.pack.img;
        pic.alt = o.pack.alt || o.pack.name;
        pic.loading = 'lazy';
        pic.width = 480;
        if (o.pack.ratio) pic.height = Math.round(480 / o.pack.ratio);
        rows.pic.appendChild(pic);
      }
      var tag = '';
      if (s.best) tag = t('best');
      else if (s.side === 'upper') tag = t('cover');
      else if (s.side === 'lower') tag = t('less');
      if (tag) {
        var flag = el('span', 'usage-calc__flag');
        flag.appendChild(el('span', 'usage-calc__flag-text', tag));
        rows.flag.appendChild(flag);
      }
      rows.name.appendChild(el('p', 'usage-calc__answer-label', optionTitle(o)));
      rows.freq.appendChild(el('p', 'usage-calc__note', o.freq.label));
      var full = type !== 'waitlist' && typeof o.pack.price === 'number' ? o.pack.price * o.qty : null;
      var club = type === 'live' && o.plan && typeof o.plan.price === 'number' ? o.plan.price * o.qty : null;
      var lead = club !== null ? club : full;
      if (lead !== null) rows.price.appendChild(el('p', 'usage-calc__answer-value', money(lead)));
      if (club !== null && t('club')) rows.price.appendChild(el('p', 'usage-calc__note', t('club')));
      if (club !== null && full !== null && full > club && t('full')) rows.price.appendChild(el('p', 'usage-calc__note', t('full') + ' ' + money(full)));
      if (full !== null && club !== null && full > club) {
        if (t('saving')) rows.price.appendChild(el('p', 'usage-calc__note', t('saving') + ' ' + money(full - club)));
        if (t('saving-year')) rows.price.appendChild(el('p', 'usage-calc__note', t('saving-year') + ' ' + money((full - club) * 12 / o.freq.months)));
      }
      var per = o.perMonth;
      var perWords = Math.abs(per - 1) < EPS && t('works-out-one') ? t('works-out-one') : t('works-out');
      if (perWords) rows.per.appendChild(el('p', 'usage-calc__note', fill(perWords, num(per, per % 1 ? 1 : 0), o.pack.name)));
      var join = data.url && o.pack.variant ? data.url + (data.url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + encodeURIComponent(o.pack.variant) : '';
      if (type === 'live' && o.plan && join && t('start')) {
        var a = el('a', 'btn usage-calc__button', t('start'));
        a.href = join + '&selling_plan=' + encodeURIComponent(o.plan.id);
        rows.action.appendChild(a);
      } else if (type === 'estimate' && join && o.pack.available === false && t('sold-out')) {
        var out = el('button', 'btn usage-calc__button', t('sold-out'));
        out.type = 'button';
        out.disabled = true;
        rows.action.appendChild(out);
      } else if (type === 'estimate' && join && t('shop') && action === 'cart') {
        var add = el('button', 'btn usage-calc__button', t('shop'));
        add.type = 'button';
        add.addEventListener('click', function () { addToCart(add, o.pack.variant, o.qty, join); });
        rows.action.appendChild(add);
      } else if (type === 'estimate' && join && t('shop')) {
        var shop = el('a', 'btn usage-calc__button', t('shop'));
        shop.href = join;
        rows.action.appendChild(shop);
      }
      return box;
    }

    var lastNumbers = {};
    root.__ucNumbers = function () { return lastNumbers; };

    // SHOP-1221: the print picker. Picking a print swaps each pack's
    // variant, price, stock, picture and plans (and the product link) for
    // that print's own, then re-suggests.
    var printBtns = Array.prototype.slice.call(root.querySelectorAll('[data-uc-print]'));
    function setPrint(i) {
      var pr = data && data.prints && data.prints[i];
      if (!pr) return;
      data.url = pr.url;
      (data.packs || []).forEach(function (pack, pi) {
        var src = pr.packs[pi] || {};
        ['variant', 'price', 'available', 'img', 'alt', 'ratio', 'plans'].forEach(function (k) { pack[k] = src[k]; });
      });
      options = buildOptions(data);
      printBtns.forEach(function (b) {
        var on = b.getAttribute('data-uc-print') === String(i);
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    if (printBtns.length > 1 && data && data.prints) {
      printBtns.forEach(function (b) {
        b.addEventListener('click', function () { setPrint(Number(b.getAttribute('data-uc-print'))); render(); });
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-uc-prints], [data-uc-prints-q]'), function (n) { n.hidden = false; });
    }

    function render() {
      var r = compute();
      var show = r.any && r.foilsWeek > 0;
      Array.prototype.forEach.call(root.querySelectorAll('[data-uc-out]'), function (n) {
        var v = show ? r[n.getAttribute('data-uc-out')] : null;
        var fmt = n.getAttribute('data-uc-fmt');
        n.textContent = v == null ? '' : fmt === 'whole' ? num(Math.round(v), 0) : num(v, 1);
      });
      if (emptyMsg) emptyMsg.hidden = show;
      var picks = show && r.boxesMonth ? suggest(options, r.boxesMonth, coverOnly) : [];
      if (plansBox) {
        while (plansBox.firstChild) plansBox.removeChild(plansBox.firstChild);
        picks.forEach(function (s) { plansBox.appendChild(card(s)); });
      }
      lastNumbers = show ? {
        'Usage Mode': mode === 'week' ? 'Weekly totals' : 'Average day',
        'Foils per Week': Math.round(r.foilsWeek),
        'Foils per Month': Math.round(r.foilsMonth),
        'Boxes per Month': r.boxesMonth == null ? null : Math.round(r.boxesMonth * 10) / 10,
        'Boxes per Year': r.boxesYear == null ? null : Math.round(r.boxesYear * 10) / 10,
        'Stylists': r.stylists,
        'Weeks Worked': r.weeks,
        'Suggested Plan': picks.map(function (s) { return optionTitle(s.opt) + ', ' + s.opt.freq.label + (s.best ? ' (best fit)' : ''); }).join(' | '),
        'Best Fit': picks.filter(function (s) { return s.best; }).map(function (s) { return optionTitle(s.opt) + ', ' + s.opt.freq.label.toLowerCase(); }).join('')
      } : {};
    }

    function save() {
      var state = { mode: mode, v: {} };
      fields.forEach(function (f) { state.v[f.getAttribute('data-uc-field')] = f.value; });
      try { window.localStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch (e) { return; }
      if (savedNote) savedNote.hidden = false;
      if (resetBtn) resetBtn.hidden = false;
    }

    function restore() {
      var raw = null;
      try { raw = window.localStorage.getItem(STORE_KEY); } catch (e) { raw = null; }
      if (!raw) return;
      var state = null;
      try { state = JSON.parse(raw); } catch (e) { state = null; }
      if (!state || !state.v) return;
      Object.keys(state.v).forEach(function (k) { if (byKey[k]) byKey[k].value = state.v[k]; });
      setMode(state.mode);
      if (savedNote) savedNote.hidden = false;
      if (resetBtn) resetBtn.hidden = false;
    }

    modeBtns.forEach(function (b) {
      b.addEventListener('click', function () { setMode(b.getAttribute('data-uc-mode')); render(); save(); });
    });
    fields.forEach(function (f) {
      f.addEventListener('input', function () { check(f); render(); save(); });
    });
    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        if (!window.confirm(t('reset-confirm'))) return;
        try { window.localStorage.removeItem(STORE_KEY); } catch (e) { /* storage blocked */ }
        fields.forEach(function (f) { f.value = starts[f.getAttribute('data-uc-field')] || ''; check(f); });
        setMode('day');
        if (savedNote) savedNote.hidden = true;
        resetBtn.hidden = true;
        render();
      });
    }

    if (modesRow) modesRow.hidden = false;
    setMode('day');
    restore();
    fields.forEach(check);
    render();
    root.classList.add('is-ready');
  }

  function boot(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-uc-root]'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();

  if (!window.__usageCalcSectionBound) {
    window.__usageCalcSectionBound = true;
    document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  }
})();
